import { z } from "zod";
/**
 * O lote: N fotos processadas de uma vez, com o mesmo personagem e a mesma
 * instrução.
 *
 * Não há tabela de lotes. Um lote são N linhas de `generations` que
 * compartilham `lote_id`, pela mesma razão que fez `capturaId` ser coluna e não
 * tabela: o agrupamento é tudo o que a tela precisa, e uma tabela custaria FK,
 * migração de dados e mais um ponto de recorte de dono para acertar. Os
 * parâmetros comuns são repetidos num `jsonb` por linha — quarenta cópias de um
 * objeto pequeno custam menos que a junção.
 *
 * Os três objetos abaixo viajam até o navegador, então moram aqui e são
 * validados por Zod: a coluna é `jsonb`, e `jsonb` sem schema é campo livre.
 */
export const dimensoesSchema = z.object({
    largura: z.number().int().positive(),
    altura: z.number().int().positive(),
    /** Simplificada: `16:9`, `4:3`. */
    proporcao: z.string(),
    orientacao: z.enum(["paisagem", "retrato", "quadrada"]),
});
export const personagemDoLoteSchema = z.object({
    /** Linhas de `image_bank` escolhidas como referência. */
    ids: z.array(z.number().int().positive()).max(4),
    /** Os `filePath` correspondentes, copiados por valor. */
    referencias: z.array(z.string().min(1)).max(4),
    nome: z.string().min(1).max(120),
    descricao: z.string().max(2000),
    /**
     * Como o personagem é, escrito **uma vez por lote** a partir das referências.
     *
     * Nulo até o `lote-preparar` rodar. Existe porque descrever o personagem foto
     * a foto produzia um personagem diferente em cada uma: num lote real a mesma
     * referência — homem calvo de barba branca e terno sem gravata — virou
     * "cabelos brancos longos e gravata borboleta" numa foto e a descrição certa
     * na outra, e o modelo de imagem seguiu o texto errado em vez da imagem.
     */
    aparencia: z.string().nullable(),
});
export const parametrosDoLoteSchema = z.object({
    /** O número do lote, de uma sequence global. É por ele que se fala do lote. */
    numero: z.number().int().positive(),
    nome: z.string().min(1).max(120),
    personagem: personagemDoLoteSchema.nullable(),
    /**
     * O que o operador pediu **além** do que o sistema já exige.
     *
     * Vazio na maioria dos lotes, e é assim que deve ser: preservar todo mundo,
     * pôr o personagem no meio, reiluminar pela cena e manter a proporção são
     * regras do sistema, não escolhas de quem opera. Este campo é para o que só
     * ele sabe — "autografando o livro", "abraçando paternalmente".
     */
    pedidoExtra: z.string().max(2000),
    modeloVisao: z.string().min(1),
});
export const analiseDaFotoSchema = z.object({
    dimensoes: dimensoesSchema,
    /**
     * O que a visão viu, chave a chave. Aberto de propósito: o conjunto de chaves
     * é da instrução, não do banco, e prender o schema aqui faria qualquer ajuste
     * na instrução virar migração.
     */
    descricao: z.record(z.string(), z.string()),
    /** `modelo` quando o próprio modelo escreveu o prompt; `montado` é o plano B. */
    promptOrigem: z.enum(["modelo", "montado"]),
    modelo: z.string(),
    custoUsd: z.number().nullable(),
    duracaoMs: z.number().int().nonnegative(),
    /**
     * A proporção pedida ao modelo de imagem — `4:5` quando o retrato alarga para
     * caber o personagem.
     *
     * Gravada aqui, junto do prompt escrito para ela, e não calculada na hora de
     * gerar: um item analisado antes de a regra existir tem um prompt que manda
     * manter a proporção original, e regerá-lo em 4:5 poria o provedor contra o
     * prompt. Ausente nesses itens, que continuam como eram.
     */
    proporcaoAlvo: z.string().optional(),
    /**
     * A proporção que de fato foi pedida ao modelo de imagem, gravada depois de gerar (#122). Difere do alvo quando o
     * modelo não aceita a que se pediu — trocado entre a análise e a geração, por exemplo. É contra ela que a
     * conferência mede o resultado.
     */
    proporcaoEnviada: z.string().optional(),
});
export const conferenciaDoItemSchema = z.object({
    pessoasOriginais: z.number().int().nonnegative().nullable(),
    pessoasNoResultado: z.number().int().nonnegative().nullable(),
    /** Quem sumiu, descrito pela roupa. Vazio quando ninguém sumiu. */
    faltando: z.string(),
    personagemPresente: z.boolean().nullable(),
    personagemFiel: z.boolean().nullable(),
    /**
     * O personagem tem o tamanho de um adulto à mesma distância das pessoas ao
     * lado — nem miniatura, nem gigante. Opcionais os dois: conferências
     * gravadas antes deles não os têm.
     */
    escalaCoerente: z.boolean().nullable().optional(),
    /** O personagem está na frente de alguém da foto, cobrindo parte do corpo. */
    cobreAlguem: z.boolean().nullable().optional(),
    proporcaoOk: z.boolean(),
    observacao: z.string(),
    veredito: z.enum(["ok", "suspeito"]),
    /** O que reprova: fato verificável. */
    motivos: z.array(z.string()),
    /**
     * O que incomoda mas é juízo, não fato. Não reprova sozinho.
     *
     * A separação nasceu de um lote real: a fidelidade do personagem reprovou as
     * duas únicas imagens que estavam certas. Presença e ausência são fato,
     * semelhança é opinião — e opinião que reprova tudo torna o retentar inútil.
     */
    ressalvas: z.array(z.string()),
    modelo: z.string(),
    custoUsd: z.number().nullable(),
});
/**
 * O estado de um item, derivado das colunas na leitura.
 *
 * Derivado, e não gravado: um contador de estado ao lado das colunas que o
 * determinam é um contador que desincroniza. A ordem abaixo é a do fluxo.
 */
export const LOTE_ESTADOS_ITEM = [
    "sem_saldo",
    "analisando",
    "gerando",
    "conferindo",
    "suspeito",
    "ok",
    "aprovado",
    "recusado",
    "abortado",
    "falhou",
];
/** O que o operador manda ao criar um lote. */
export const criarLoteSchema = z.object({
    nome: z.string().min(1, "Dê um nome ao lote").max(120),
    /** Já enviadas por `POST /api/generations/upload-photo`, uma requisição por foto. */
    fotos: z
        .array(z.object({
        path: z.string().min(1),
        nomeOriginal: z.string().max(200),
    }))
        .min(1, "Envie ao menos uma foto")
        .max(200),
    personagemIds: z.array(z.number().int().positive()).max(4).default([]),
    personagemNome: z.string().max(120).default(""),
    personagemDescricao: z.string().max(2000).default(""),
    pedidoExtra: z.string().max(2000).default(""),
    /**
     * Obrigatória: é o que liga as fotos de volta ao evento que as produziu.
     *
     * Sem ela um lote de quarenta vira quarenta capturas soltas na Biblioteca, e o
     * operador não tem por onde puxá-las. O local chega pela vivência.
     */
    vivenciaId: z.string().min(1, "Selecione a vivência"),
});
//# sourceMappingURL=lote.js.map